import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { MESES_CXC, type ReporteCxc } from '@leadsphere/types';

import { useApi } from '../../hooks/useApi.js';
import { BloqueCobranza } from './BloqueCobranza.js';
import { FiltrosReportes, type FiltrosUI } from './FiltrosReportes.js';
import { GraficaFacturado, GraficaFlujo } from './GraficasResumen.js';
import { TablaConcentracion } from './TablaConcentracion.js';
import { TarjetasResumen } from './TarjetasResumen.js';

function aQuery(f: FiltrosUI | null): string {
  if (!f) return '';
  const p = new URLSearchParams();
  if (f.anios.length) p.set('anios', f.anios.join(','));
  if (f.meses.length) p.set('meses', f.meses.join(','));
  if (f.clientes.length) p.set('clientes', f.clientes.join(','));
  if (f.iva) p.set('iva', '1');
  if (!f.adicionales) p.set('adicionales', '0');
  const s = p.toString();
  return s ? `?${s}` : '';
}

// Lo que el servidor aplicó de verdad (p. ej. "todos los años" si se
// deseleccionaron), para que los filtros vacíos no se lean como "nada".
function textoAplicado(r: ReporteCxc): string {
  const a = r.filtros_aplicados;
  const anios = a.anios.length ? a.anios.join(', ') : 'sin años con registros';
  const meses = a.meses.length === MESES_CXC.length ? 'todos los meses' : a.meses.join(', ');
  const clientes = a.clientes.length ? `${a.clientes.length} cliente(s)` : 'todos los clientes';
  return `Mostrando: ${anios} · ${meses} · ${clientes}${a.iva ? ' · con IVA' : ''}${a.adicionales ? '' : ' · solo fees de servicio'}`;
}

function desdeRespuesta(r: ReporteCxc): FiltrosUI {
  const a = r.filtros_aplicados;
  return { anios: a.anios.map(String), meses: a.meses, clientes: a.clientes, iva: a.iva, adicionales: a.adicionales };
}

// Spec 2026-10-14-cxc-reportes-design.md. La primera carga va sin filtros:
// el servidor elige el periodo por defecto y la pantalla lo refleja.
export function ReportesPage() {
  const { get } = useApi();
  const [filtros, setFiltros] = useState<FiltrosUI | null>(null);
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['cxc-reportes', filtros],
    queryFn: () => get<ReporteCxc>(`/cxc/reportes${aQuery(filtros)}`),
    placeholderData: prev => prev,
  });

  if (isLoading && !data) return <div className="reportes-vacio">Cargando reportes…</div>;
  if (!data) return <div className="form-error-banner">No se pudieron cargar los reportes.</div>;

  const ultimo = data.filtros_aplicados.ultimo_periodo;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Reportes</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Indicadores y análisis de cuentas por cobrar</div>
      </div>

      <FiltrosReportes valor={filtros ?? desdeRespuesta(data)} opciones={data.opciones} onChange={setFiltros} />
      <div className="reportes-nota">
        {textoAplicado(data)}{isFetching ? ' · Actualizando…' : ''}
      </div>

      {isError && (
        <div className="form-error-banner">No se pudieron actualizar los reportes; se muestran los anteriores.</div>
      )}

      {data.entradas_no_mxn > 0 && (
        <div className="form-error-banner">
          {data.entradas_no_mxn} entradas no están en MXN y se suman sin conversión de tipo de cambio.
        </div>
      )}

      {!ultimo ? (
        <div className="reportes-vacio">Aún no hay entradas de CxC.</div>
      ) : (
        <>
          <div className="reportes-seccion-titulo">Resumen</div>
          <TarjetasResumen t={data.resumen.tarjetas} />
          <div className="reportes-grid-2">
            <GraficaFacturado datos={data.resumen.tendencia} ultimo={ultimo} />
            <GraficaFlujo datos={data.resumen.flujo} ultimo={ultimo} sinFecha={data.resumen.cobros_sin_fecha_pago} />
          </div>
          <TablaConcentracion filas={data.resumen.concentracion} />

          <div className="reportes-seccion-titulo">Cobranza (saldo hasta {ultimo.mes} {ultimo.anio})</div>
          <BloqueCobranza c={data.cobranza} />
        </>
      )}
    </div>
  );
}
